import ls from "local-storage";
import axios from "axios";
import { decamelizeKeys } from "humps";
import _ from "lodash";

import AuthService from "./AuthService";

class BaseService {
  static apiUrl({ app } = { app: false }) {
    const api = process.env.VUE_APP_API_URL;
    let subdomain = ls(process.env.VUE_APP_LS_SUBDOMAIN) || "public";
    let url = api.replace("public", subdomain);

    if (!app && this.isUser()) {
      url = process.env.VUE_APP_USER_API_URL;
      subdomain = window.location.hostname.match(/(.*)\.app/);
      if (subdomain) {
        url = url.replace("public", subdomain[1]);
      }
    }
    return url;
  }

  static isUser() {
    return /.*\.app\./.test(window.location.hostname);
  }

  static setSubdomain(subdomain) {
    ls(process.env.VUE_APP_LS_SUBDOMAIN, subdomain);
  }

  static getSubdomain() {
    return ls(process.env.VUE_APP_LS_SUBDOMAIN);
  }

  static pageSize() {
    return 50;
  }

  static get(url) {
    return this.request("get", url, null);
  }

  static post(type, url, data) {
    const requestData = {
      data: {
        type,
        attributes: decamelizeKeys(data)
      }
    };

    return this.request("post", url, requestData);
  }

  static patch(type, url, data) {
    const requestData = {
      data: {
        id: data.id,
        type,
        attributes: decamelizeKeys(data)
      }
    };

    return this.request("patch", url, requestData);
  }

  static delete(url) {
    return this.request("delete", url);
  }

  /**
   * if the token expires in 5 min or less, refresh it.
   * this is easier than trying to queue up and then replay requests after the token has expired
   *
   * @param token
   * @returns {boolean}
   */
  static isTokenAlmostExpired(token) {
    const expires = new Date(0);
    expires.setUTCSeconds(token.created_at + token.expires_in - 5 * 60);

    if (expires === null) {
      return false;
    }

    // Token expired?
    if (!(expires.valueOf() > new Date().valueOf())) {
      AuthService.refreshToken();
    }

    return null;
  }

  static request(requestType, url, data = null) {
    const user = ls.get(process.env.VUE_APP_LS_USER);

    if (_.has(user, "jwt")) {
      axios.defaults.headers.common.Authorization = `Bearer ${
        user.jwt.access_token
      }`;

      const config = {};
      if (requestType === "post" || requestType === "patch") {
        config.headers = {
          "Content-Type": "application/vnd.api+json"
        };
      }

      axios.interceptors.response.use(
        response => {
          this.isTokenAlmostExpired(user.jwt);
          return response;
        },
        error => {
          // Do something with response error
          if (error.response.status === 401) {
            AuthService.logout();
            window.location = "/";
          }
          return Promise.reject(error);
        }
      );

      return axios[requestType](`${this.apiUrl()}/v1/${url}`, data, config);
    }
    return Promise.reject();
  }
}

export default BaseService;
